/**
 * Marketing → Campaign Breakdown: the spend tree (campaign → adset → ad, from
 * ad_spend) with attribution credit laid over it.
 *
 * Credit comes from /marketing/campaigns/attribution, which runs the Channels
 * report's engine and files each credited share under the deepest node its
 * touch's UTMs prove. Both payloads key nodes identically (the server's
 * campaignTreeKeys), so merging is a lookup, never a name match here.
 */

export const CAMPAIGN_COLUMNS = [
  { key: 'spend', label: 'Spend', description: 'Ad spend the platform reported for these dates.', type: 'money' },
  { key: 'clicks', label: 'Clicks', description: 'Ad clicks the platform reported.', type: 'count' },
  { key: 'impressions', label: 'Impressions', description: 'Ad impressions the platform reported.', type: 'count' },
  { key: 'ctr', label: 'CTR', description: 'Clicks ÷ Impressions.', type: 'percent', hiddenByDefault: true },
  { key: 'cpc', label: 'CPC', description: 'Spend ÷ Clicks.', type: 'money2', lowerIsBetter: true },
  { key: 'cpm', label: 'CPM', description: 'Spend per 1,000 impressions.', type: 'money2', lowerIsBetter: true },
  { key: 'cpmr', label: 'CPMr', description: 'Spend per 1,000 people reached (Meta only). Hover for frequency; the ad-level sparkline tracks fatigue.', type: 'money2', lowerIsBetter: true },
  { key: 'quality', label: 'Quality', description: 'Traffic quality score — expected conversion %, based on how deep this ad’s traffic gets on the site. Ads only.', type: 'quality' },
  { key: 'spend_share', label: '% of spend', description: 'This row’s share of all spend in these dates.', type: 'percent' },
  { key: 'revenue', label: 'Conversion value', description: 'Revenue the selected attribution model credits to this row. Includes its adsets and ads.', type: 'money', attributed: true },
  { key: 'orders', label: 'Purchases', description: 'Paid orders the model credits to this row. Fractional when a model splits an order.', type: 'count', attributed: true },
  { key: 'new_orders', label: 'New customers', description: 'Credit for first purchases (not renewals).', type: 'count', attributed: true },
  { key: 'cac', label: 'CAC', description: 'Spend ÷ New customers.', type: 'money', lowerIsBetter: true, attributed: true },
  { key: 'roas', label: 'ROAS', description: 'Conversion value ÷ Spend.', type: 'multiple', attributed: true },
  { key: 'aov', label: 'AOV', description: 'Conversion value ÷ Purchases.', type: 'money', attributed: true, hiddenByDefault: true },
  { key: 'profit', label: 'Net revenue', description: 'Conversion value − Spend.', type: 'money', attributed: true, hiddenByDefault: true },
  { key: 'click_to_sale', label: 'Click → purchase', description: 'Purchases ÷ platform clicks. Two registers — read it as indicative.', type: 'percent', attributed: true, hiddenByDefault: true },
]

export const DEFAULT_CAMPAIGN_COLUMNS = CAMPAIGN_COLUMNS.filter(column => !column.hiddenByDefault).map(column => column.key)

export function normalizeCampaignColumns(value) {
  const known = new Set(CAMPAIGN_COLUMNS.map(column => column.key))
  const order = [...new Set([...(Array.isArray(value?.order) ? value.order : []), ...known])].filter(key => known.has(key))
  const visible = Array.isArray(value?.visible) ? [...new Set(value.visible.filter(key => known.has(key)))] : DEFAULT_CAMPAIGN_COLUMNS
  return { order, visible: visible.length ? visible : DEFAULT_CAMPAIGN_COLUMNS }
}

/** The key of the row holding credit a touch could prove only to the campaign. */
export const CAMPAIGN_LEVEL_KEY = '__campaign_level__'

const ratio = (numerator, denominator, scale = 1) =>
  numerator == null || denominator == null || !denominator ? null : numerator / denominator * scale

const NO_CREDIT = { orders: null, revenue: null, new_orders: null }
const ZERO_CREDIT = { orders: 0, revenue: 0, new_orders: 0 }
const creditOf = item => item ? { orders: item.orders, revenue: item.revenue, new_orders: item.new_orders } : ZERO_CREDIT
const emptySpend = { spend: 0, clicks: 0, impressions: 0, reach: 0 }

/** Every ratio a row derives; a missing or zero denominator is null, never 0. */
export function withCampaignMetrics(row, totalSpend) {
  const hasSpend = row.spend != null
  return { ...row,
    ctr: ratio(row.clicks, row.impressions, 100),
    cpc: row.spend > 0 ? ratio(row.spend, row.clicks) : null,
    cpm: row.spend > 0 ? ratio(row.spend, row.impressions, 1000) : null,
    cpmr: row.cpmr > 0 ? row.cpmr : null,
    spend_share: hasSpend ? ratio(row.spend, totalSpend, 100) : null,
    cac: hasSpend && row.spend > 0 ? ratio(row.spend, row.new_orders) : null,
    roas: row.spend > 0 && row.revenue != null ? row.revenue / row.spend : null,
    aov: ratio(row.revenue, row.orders),
    profit: hasSpend && row.revenue != null ? row.revenue - row.spend : null,
    click_to_sale: ratio(row.orders, row.clicks, 100),
  }
}

/**
 * Lay credit over the spend tree.
 *
 * With no credit (not loaded, or unavailable for this clinic) every attributed
 * figure is null — unmeasured. With credit, a spending node nobody credited
 * shows a real 0. Credited nodes with no spend in the window (an ad that
 * stopped, clicked inside the lookback) are added with $0 spend, because
 * dropping them would leave a campaign's ads summing to less than it.
 */
export function mergeCampaignCredit(tree, credit) {
  const campaigns = tree?.campaigns || []
  const totalSpend = tree?.summary?.total_spend ?? campaigns.reduce((sum, c) => sum + (c.spend || 0), 0)
  const loaded = Boolean(credit)
  const derive = row => withCampaignMetrics(row, totalSpend)
  const byKey = list => new Map((list || []).map(item => [item.key, item]))

  const creditedCampaigns = byKey(credit?.campaigns)
  const merged = campaigns.map(campaign => {
    const campaignCredit = creditedCampaigns.get(campaign.key)
    creditedCampaigns.delete(campaign.key)
    return mergeCampaign(campaign, campaignCredit, loaded, derive)
  })
  for (const orphan of creditedCampaigns.values()) {
    merged.push(mergeCampaign({
      key: orphan.key, channel: orphan.channel, campaign_id: orphan.campaign_id,
      campaign_name: orphan.campaign_name || `Campaign ${orphan.campaign_id || 'unknown'}`,
      ...emptySpend, adsets: [], noSpend: true,
    }, orphan, loaded, derive))
  }
  return merged
}

function mergeCampaign(campaign, campaignCredit, loaded, derive) {
  const creditedAdsets = new Map((campaignCredit?.adsets || []).map(item => [item.key, item]))
  const adsets = (campaign.adsets || []).map(adset => {
    const adsetCredit = creditedAdsets.get(adset.key)
    creditedAdsets.delete(adset.key)
    return mergeAdset(adset, adsetCredit, loaded, derive)
  })
  for (const orphan of creditedAdsets.values()) {
    adsets.push(mergeAdset({ key: orphan.key, adset_id: orphan.adset_id,
      adset_name: orphan.adset_name || `Adset ${orphan.adset_id || 'unknown'}`, ...emptySpend, ads: [], noSpend: true }, orphan, loaded, derive))
  }
  // Credit proven only to the campaign — every Google click, and Meta links
  // whose adset could not be told apart. Shown as its own row so a campaign's
  // children visibly add up to it.
  if (campaignCredit) {
    const placed = (campaignCredit.adsets || []).reduce((sum, item) => ({
      orders: sum.orders + item.orders, revenue: sum.revenue + item.revenue, new_orders: sum.new_orders + item.new_orders,
    }), { ...ZERO_CREDIT })
    const rest = { orders: campaignCredit.orders - placed.orders, revenue: campaignCredit.revenue - placed.revenue,
      new_orders: campaignCredit.new_orders - placed.new_orders }
    if (rest.orders > 1e-9 || rest.revenue > 1e-6) {
      adsets.push(derive({ key: CAMPAIGN_LEVEL_KEY, adset_name: 'Credited to the campaign only', synthetic: true,
        spend: null, clicks: null, impressions: null, ...rest, ads: [] }))
    }
  }
  return derive({ ...campaign, ...(loaded ? creditOf(campaignCredit) : NO_CREDIT), adsets })
}

function mergeAdset(adset, adsetCredit, loaded, derive) {
  const creditedAds = new Map((adsetCredit?.ads || []).map(item => [item.key, item]))
  const ads = (adset.ads || []).map(ad => {
    const adCredit = creditedAds.get(ad.key)
    creditedAds.delete(ad.key)
    return derive({ ...ad, ...(loaded ? creditOf(adCredit) : NO_CREDIT) })
  })
  for (const orphan of creditedAds.values()) {
    ads.push(derive({ key: orphan.key, ad_id: orphan.ad_id, ad_name: orphan.ad_name || `Ad ${orphan.ad_id || 'unknown'}`,
      ...emptySpend, noSpend: true, ...creditOf(orphan) }))
  }
  return derive({ ...adset, ...(loaded ? creditOf(adsetCredit) : NO_CREDIT), ads })
}

/** Sort a level, keeping unmeasured values below measured ones (zeros included). */
export function sortCampaignRows(rows, { key, direction }) {
  const sign = direction === 'asc' ? 1 : -1
  const nameOf = row => row.campaign_name || row.adset_name || row.ad_name || ''
  return [...rows].sort((a, b) => {
    if (a.synthetic !== b.synthetic) return a.synthetic ? 1 : -1
    if (key === 'name') return sign * nameOf(a).localeCompare(nameOf(b))
    if (key === 'channel') return sign * String(a.channel || '').localeCompare(String(b.channel || '')) || nameOf(a).localeCompare(nameOf(b))
    const av = a[key]
    const bv = b[key]
    if (av == null && bv != null) return 1
    if (bv == null && av != null) return -1
    return sign * ((av ?? 0) - (bv ?? 0)) || nameOf(a).localeCompare(nameOf(b))
  })
}
